"use client"

import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { createPortal } from "react-dom"
import HoverTooltip from "../tooltip/HoverTooltip"
import { getBrandSearchLabel, getPropertySearchLabel, normalizeDropdownOption, safeHotelSearchDisplayText } from "../../../lib/utils/hotelSearchMappers"
import "./SearchableDropdown.css"

function FieldLabelInfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.2 9.00078C16.2 10.9103 15.4415 12.7417 14.0912 14.092C12.741 15.4422 10.9096 16.2008 9.00005 16.2008C7.09049 16.2008 5.25914 15.4422 3.90888 14.092C2.55862 12.7417 1.80005 10.9103 1.80005 9.00078C1.80005 7.09122 2.55862 5.25987 3.90888 3.90961C5.25914 2.55935 7.09049 1.80078 9.00005 1.80078C10.9096 1.80078 12.741 2.55935 14.0912 3.90961C15.4415 5.25987 16.2 7.09122 16.2 9.00078V9.00078ZM9.00005 6.30078C8.84192 6.30063 8.68654 6.34214 8.54955 6.42114C8.41257 6.50014 8.29881 6.61383 8.21975 6.75078C8.16266 6.85687 8.08485 6.95042 7.99093 7.02588C7.89702 7.10133 7.78891 7.15717 7.67301 7.19007C7.55712 7.22297 7.4358 7.23227 7.31624 7.21742C7.19669 7.20256 7.08133 7.16386 6.97701 7.10359C6.8727 7.04333 6.78154 6.96273 6.70895 6.86658C6.63636 6.77043 6.58381 6.66069 6.55441 6.54386C6.52502 6.42702 6.51939 6.30548 6.53784 6.18642C6.5563 6.06737 6.59846 5.95323 6.66185 5.85078C6.95905 5.33608 7.41777 4.93383 7.96687 4.7064C8.51598 4.47897 9.12478 4.43907 9.69887 4.5929C10.273 4.74673 10.7802 5.08569 11.1421 5.5572C11.5039 6.02872 11.7 6.60644 11.7 7.20078C11.7002 7.75934 11.5272 8.3042 11.2048 8.76035C10.8825 9.21649 10.4266 9.56147 9.90005 9.74778V9.90078C9.90005 10.1395 9.80523 10.3684 9.63644 10.5372C9.46766 10.706 9.23874 10.8008 9.00005 10.8008C8.76135 10.8008 8.53244 10.706 8.36365 10.5372C8.19487 10.3684 8.10005 10.1395 8.10005 9.90078V9.00078C8.10005 8.76209 8.19487 8.53317 8.36365 8.36439C8.53244 8.1956 8.76135 8.10078 9.00005 8.10078C9.23874 8.10078 9.46766 8.00596 9.63644 7.83718C9.80523 7.66839 9.90005 7.43948 9.90005 7.20078C9.90005 6.96209 9.80523 6.73317 9.63644 6.56439C9.46766 6.3956 9.23874 6.30078 9.00005 6.30078ZM9.00005 13.5008C9.23874 13.5008 9.46766 13.406 9.63644 13.2372C9.80523 13.0684 9.90005 12.8395 9.90005 12.6008C9.90005 12.3621 9.80523 12.1332 9.63644 11.9644C9.46766 11.7956 9.23874 11.7008 9.00005 11.7008C8.76135 11.7008 8.53244 11.7956 8.36365 11.9644C8.19487 12.1332 8.10005 12.3621 8.10005 12.6008C8.10005 12.8395 8.19487 13.0684 8.36365 13.2372C8.53244 13.406 8.76135 13.5008 9.00005 13.5008V13.5008Z"
        fill="#BDBDBD"
      />
    </svg>
  )
}

const MENU_GAP_PX = 4
const MENU_MAX_HEIGHT_PX = 280
const MENU_MIN_SPACE_PX = 200

function nodeContains(container, target) {
  return target instanceof Node && Boolean(container?.contains(target))
}

function resolveDropdownOptionText(option) {
  return safeHotelSearchDisplayText(option?.label ?? option?.value ?? option, {
    preferProperty: Boolean(option?.sub_brand ?? option?.subBrand),
  }) || safeHotelSearchDisplayText(option, {
    preferProperty: Boolean(option?.sub_brand ?? option?.subBrand),
  })
}

const SearchableDropdown = ({
  id,
  name,
  label,
  value,
  onChange,
  onSearch,
  options,
  placeholder,
  required = false,
  disabled = false,
  loading = false,
  error = null,
  tooltip = null,
  disableEnterSearch = false,
  disableCustomKeyword = false,
  /** Render menu in document.body with fixed positioning (recommended inside modals). */
  menuPortal = false,
  /** Hide the field label row (e.g. compact settings property pickers). */
  hideLabel = false,
  /** Placeholder for the in-menu search input; defaults from `label` when omitted. */
  searchPlaceholder = null,
  /** Hide the in-menu search field (scrollable list only). */
  hideSearch = false,
  /** Fetch next page when the options list is scrolled near the bottom. */
  onLoadMore = null,
  loadingMore = false,
  hasMore = true,
  /** When set, selection highlight uses `option.id` instead of label/value text. */
  selectedId = null,
  /** Secondary line under the trigger value (e.g. property location). */
  valueSubLabel = null,
  /** Extra class on the root wrapper. */
  wrapperClassName = "",
  /** Extra class on the portaled/non-portaled options menu. */
  menuClassName = "",
  /** z-index for the portaled menu (raise above high-z-index modals). */
  menuZIndex = null,
  /** Minimum menu width in px (widens narrow triggers; use with menuAlign). */
  menuMinWidth = null,
  /** When wider than trigger: "left" keeps left edge aligned; "right" keeps right edge aligned. */
  menuAlign = "left",
  /** Horizontal nudge for portaled menu position in px (negative shifts left). */
  menuOffsetX = 0,
  /** When true, map raw hotel search API rows to safe string labels. */
  normalizeHotelSearchOptions = false,
  /** Use with normalizeHotelSearchOptions for property (sub-brand) dropdowns. */
  preferPropertyOption = false,
  /** Shown when local filter returns no options (requires a non-empty search query). */
  emptyResultsMessage = "No results found",
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isSearching, setIsSearching] = useState(false)
  const [menuStyle, setMenuStyle] = useState(null)
  const [openAbove, setOpenAbove] = useState(false)
  const wrapperRef = useRef(null)
  const containerRef = useRef(null)
  const popoverRef = useRef(null)
  const optionsListRef = useRef(null)
  const loadMoreSentinelRef = useRef(null)
  const inputRef = useRef(null)
  const searchTimeoutRef = useRef(null)
  const lastSearchedQueryRef = useRef(null)
  const lastQueryRef = useRef("")
  const suppressTriggerRef = useRef(false)
  const loadMoreLockRef = useRef(false)
  const loadingMoreRef = useRef(false)
  const performSearchRef = useRef(null)
  const isRemoteSearch = typeof onSearch === "function"
  const supportsLoadMore = typeof onLoadMore === "function"

  const safeOptions = useMemo(() => {
    const list = Array.isArray(options) ? options : []
    if (!normalizeHotelSearchOptions) return list
    return list
      .map((item) => normalizeDropdownOption(item, { preferProperty: preferPropertyOption }))
      .filter((opt) => typeof opt.label === "string" && opt.label.trim())
  }, [options, normalizeHotelSearchOptions, preferPropertyOption])

  const displayValue = useMemo(
    () =>
      safeHotelSearchDisplayText(value, { preferProperty: preferPropertyOption }),
    [value, preferPropertyOption],
  )

  const displayOptions = useMemo(() => {
    if (isRemoteSearch) return safeOptions

    const q = query.trim().toLowerCase()
    if (!q) return safeOptions

    return safeOptions.filter((option) => {
      const text = resolveDropdownOptionText(option)
      const sub = option.subLabel ?? ""
      return (
        text.toLowerCase().includes(q) || String(sub).toLowerCase().includes(q)
      )
    })
  }, [safeOptions, query, isRemoteSearch])

  const isOptionSelected = useCallback(
    (option) => {
      if (
        selectedId != null &&
        selectedId !== "" &&
        option?.id != null &&
        String(option.id) !== ""
      ) {
        return String(option.id) === String(selectedId)
      }
      const text = resolveDropdownOptionText(option)
      const current = safeHotelSearchDisplayText(value, { preferProperty: preferPropertyOption })
      return text === current
    },
    [selectedId, value, preferPropertyOption],
  )

  const normalizedQuery = query.trim()

  const hasExactOptionMatch = useMemo(() => {
    if (!normalizedQuery) return false
    const q = normalizedQuery.toLowerCase()
    return displayOptions.some((option) => {
      const text = resolveDropdownOptionText(option)
      return text.toLowerCase() === q
    })
  }, [displayOptions, normalizedQuery])

  const showCustomOption =
    !disableCustomKeyword && normalizedQuery && !hasExactOptionMatch

  const handleSelect = useCallback(
    (option) => {
      const text = resolveDropdownOptionText(option)
      suppressTriggerRef.current = true
      onChange(text, option)

      setQuery("")
      setActiveIndex(-1)
      setIsSearching(false)
      lastSearchedQueryRef.current = null

      setOpen(false)
      window.setTimeout(() => {
        suppressTriggerRef.current = false
      }, 200)
    },
    [onChange],
  )

  const selectOption = useCallback(
    (event, option) => {
      event.preventDefault()
      event.stopPropagation()
      handleSelect(option)
    },
    [handleSelect],
  )

  const performSearch = useCallback(
    async (searchQuery, { force = false } = {}) => {
      if (!isRemoteSearch) {
        setIsSearching(false)
        return
      }

      const normalized = searchQuery ?? ""
      if (
        !force &&
        lastSearchedQueryRef.current != null &&
        lastSearchedQueryRef.current === normalized &&
        (normalized !== "" || displayOptions.length > 0 || loading)
      ) {
        setIsSearching(false)
        return
      }

      lastSearchedQueryRef.current = normalized
      setIsSearching(true)

      try {
        const result = onSearch(normalized, { force })
        if (result != null && typeof result.then === "function") {
          await result
        }
      } catch {
        /* parent / thunk handles errors */
      } finally {
        setIsSearching(false)
      }
    },
    [isRemoteSearch, onSearch, displayOptions.length, loading],
  )

  performSearchRef.current = performSearch

  useEffect(() => {
    if (!open) return

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    const trimmed = query.trim()
    const prev = lastQueryRef.current
    lastQueryRef.current = trimmed

    if (prev !== "" && trimmed === "") {
      if (isRemoteSearch) {
        performSearchRef.current?.("", { force: true })
      }
      return
    }

    if (trimmed !== "" && isRemoteSearch) {
      searchTimeoutRef.current = setTimeout(() => {
        performSearchRef.current?.(trimmed)
      }, 250)
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [query, open, isRemoteSearch])

  const onKeyDown = (e) => {
    if (!open) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      const maxIndex = showCustomOption ? displayOptions.length : displayOptions.length - 1
      setActiveIndex((prev) => Math.min(prev + 1, maxIndex))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (activeIndex >= 0) {
        if (showCustomOption && activeIndex === displayOptions.length) {
          handleSelect({ label: query.trim(), value: query.trim() })
        } else if (activeIndex < displayOptions.length) {
          handleSelect(displayOptions[activeIndex])
        }
      } else if (!disableEnterSearch && query.trim() && isRemoteSearch) {
        performSearch(query.trim())
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      setOpen(false)
      setActiveIndex(-1)
      setIsSearching(false)
    }
  }

  const handleDropdownClick = (event) => {
    if (disabled) return
    if (suppressTriggerRef.current) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    if (open) {
      setOpen(false)
      setActiveIndex(-1)
      setIsSearching(false)
      return
    }

    setOpen(true)
    setQuery("")
    setActiveIndex(-1)
    lastQueryRef.current = ""

    if (isRemoteSearch) {
      lastSearchedQueryRef.current = null
      performSearch("", { force: true })
    }

    if (!hideSearch) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }

  const updateMenuPosition = useCallback(() => {
    if (!menuPortal || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX
    const spaceAbove = rect.top - MENU_GAP_PX
    const shouldOpenAbove = spaceBelow < MENU_MIN_SPACE_PX && spaceAbove > spaceBelow

    setOpenAbove(shouldOpenAbove)

    const minWidth = typeof menuMinWidth === "number" && menuMinWidth > 0 ? menuMinWidth : 0
    const width = Math.max(rect.width, minWidth)
    let left = rect.left
    if (menuAlign === "right" && width > rect.width) {
      left = rect.right - width
    }
    const edgeMargin = 8
    const offsetX = typeof menuOffsetX === "number" ? menuOffsetX : 0
    left = left + offsetX
    left = Math.max(edgeMargin, Math.min(left, window.innerWidth - width - edgeMargin))

    setMenuStyle({
      position: "fixed",
      left,
      width,
      zIndex: typeof menuZIndex === "number" && menuZIndex > 0 ? menuZIndex : 10050,
      maxHeight: Math.min(
        MENU_MAX_HEIGHT_PX,
        Math.max(120, shouldOpenAbove ? spaceAbove - MENU_GAP_PX : spaceBelow - MENU_GAP_PX),
      ),
      ...(shouldOpenAbove
        ? { bottom: window.innerHeight - rect.top + MENU_GAP_PX }
        : { top: rect.bottom + MENU_GAP_PX }),
    })
  }, [menuPortal, menuMinWidth, menuAlign, menuOffsetX, menuZIndex])

  const menuMaxHeightPx = menuStyle?.maxHeight

  const getScrollRoot = useCallback(() => optionsListRef.current, [])

  useEffect(() => {
    if (!open || !menuPortal) return

    updateMenuPosition()
    window.addEventListener("resize", updateMenuPosition)
    window.addEventListener("scroll", updateMenuPosition, true)

    return () => {
      window.removeEventListener("resize", updateMenuPosition)
      window.removeEventListener("scroll", updateMenuPosition, true)
    }
  }, [open, menuPortal, updateMenuPosition])

  useEffect(() => {
    const handlePointerDownOutside = (event) => {
      const target = event.target
      if (nodeContains(wrapperRef.current, target)) return
      if (nodeContains(popoverRef.current, target)) return

      setOpen(false)
      setActiveIndex(-1)
      setIsSearching(false)
    }

    if (open) {
      document.addEventListener("mousedown", handlePointerDownOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside)
    }
  }, [open])

  const listLoading = loading || isSearching
  const initialListLoading = loading && (options?.length ?? 0) === 0
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? (label ? `Search ${label.toLowerCase()}...` : "Search...")
  const ariaFieldLabel = label || "options"

  const triggerLoadMore = useCallback(() => {
    if (!supportsLoadMore || !hasMore || loadingMore || isSearching || initialListLoading) return
    if (loadMoreLockRef.current || loadingMoreRef.current) return

    loadMoreLockRef.current = true
    loadingMoreRef.current = true
    const result = onLoadMore()

    const release = () => {
      loadMoreLockRef.current = false
      loadingMoreRef.current = false
    }

    if (result != null && typeof result.then === "function") {
      result.finally(release)
    } else if (result != null && typeof result.finally === "function") {
      result.finally(release)
    } else {
      release()
    }
  }, [supportsLoadMore, hasMore, loadingMore, isSearching, initialListLoading, onLoadMore])

  const isNearScrollEnd = useCallback((el) => {
    if (!el) return false
    return el.scrollTop + el.clientHeight >= el.scrollHeight - 60
  }, [])

  const checkScrollLoadMore = useCallback(() => {
    const root = getScrollRoot()
    if (!root || !isNearScrollEnd(root)) return
    triggerLoadMore()
  }, [getScrollRoot, isNearScrollEnd, triggerLoadMore])

  useEffect(() => {
    if (!loadingMore) {
      loadingMoreRef.current = false
    }
  }, [loadingMore])

  const handleOptionsScroll = useCallback(() => {
    checkScrollLoadMore()
  }, [checkScrollLoadMore])

  useEffect(() => {
    if (!open || !supportsLoadMore) return undefined

    const onScroll = () => {
      checkScrollLoadMore()
    }

    const attached = []

    const attach = () => {
      const scrollRoot = getScrollRoot()
      if (scrollRoot && !attached.includes(scrollRoot)) {
        scrollRoot.addEventListener("scroll", onScroll, { passive: true })
        attached.push(scrollRoot)
      }
    }

    attach()
    const frameId = requestAnimationFrame(attach)

    return () => {
      cancelAnimationFrame(frameId)
      attached.forEach((el) => el.removeEventListener("scroll", onScroll))
    }
  }, [open, supportsLoadMore, checkScrollLoadMore, displayOptions.length, getScrollRoot])

  useEffect(() => {
    if (!open || !supportsLoadMore || !hasMore || loadingMore || isSearching || initialListLoading) {
      return undefined
    }

    let observer = null
    let frameId = 0

    const attach = () => {
      const root = getScrollRoot()
      const sentinel = loadMoreSentinelRef.current
      if (!root || !sentinel) return

      observer?.disconnect()
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            triggerLoadMore()
          }
        },
        { root, rootMargin: "120px", threshold: 0 },
      )
      observer.observe(sentinel)
    }

    attach()
    frameId = requestAnimationFrame(attach)

    return () => {
      cancelAnimationFrame(frameId)
      observer?.disconnect()
    }
  }, [
    open,
    supportsLoadMore,
    hasMore,
    loadingMore,
    isSearching,
    initialListLoading,
    displayOptions.length,
    triggerLoadMore,
    getScrollRoot,
  ])

  useEffect(() => {
    if (!open || !supportsLoadMore || !hasMore || loadingMore || isSearching || initialListLoading) {
      return undefined
    }

    let frameId = 0

    const checkUnderfill = () => {
      const root = getScrollRoot()
      if (!root) return
      if (root.scrollHeight <= root.clientHeight + 1) {
        triggerLoadMore()
      }
    }

    checkUnderfill()
    frameId = requestAnimationFrame(checkUnderfill)

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [
    open,
    supportsLoadMore,
    hasMore,
    loadingMore,
    isSearching,
    initialListLoading,
    displayOptions.length,
    triggerLoadMore,
    getScrollRoot,
    menuMaxHeightPx,
  ])

  useEffect(() => {
    if (!open) {
      loadMoreLockRef.current = false
    }
  }, [open, query])

  const popoverClassName = [
    "searchable-dropdown-popover",
    openAbove ? "searchable-dropdown-popover--above" : "",
    menuPortal ? "searchable-dropdown-popover--portaled" : "",
    hideSearch ? "searchable-dropdown-popover--no-search" : "",
    menuClassName,
  ]
    .filter(Boolean)
    .join(" ")

  const clearActiveOption = useCallback(() => {
    setActiveIndex(-1)
  }, [])

  const popoverNode = (
    <div
      ref={popoverRef}
      className={popoverClassName}
      style={
        menuPortal
          ? { ...menuStyle, height: menuStyle?.maxHeight }
          : undefined
      }
      role="dialog"
      onMouseDown={(event) => event.stopPropagation()}
      onMouseLeave={(event) => {
        const next = event.relatedTarget
        if (nodeContains(popoverRef.current, next)) return
        clearActiveOption()
      }}
    >
      {!hideSearch ? (
        <div className="searchable-dropdown-search-wrapper">
          <input
            ref={inputRef}
            className="searchable-dropdown-search"
            type="text"
            placeholder={resolvedSearchPlaceholder}
            aria-label={resolvedSearchPlaceholder}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIndex(-1)
            }}
            onKeyDown={onKeyDown}
          />
          {(isSearching || loading) && (
            <div className="searchable-dropdown-loader">
              <svg className="spinner" viewBox="0 0 50 50">
                <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
              </svg>
            </div>
          )}
        </div>
      ) : null}
      <ul
        ref={optionsListRef}
        id={`${id}-listbox`}
        role="listbox"
        className="searchable-dropdown-options"
        aria-label={`${ariaFieldLabel} options`}
        onScroll={supportsLoadMore ? handleOptionsScroll : undefined}
        onMouseLeave={clearActiveOption}
      >
        {initialListLoading ? (
          <li className="searchable-dropdown-loading" role="status" aria-live="polite">
            Loading…
          </li>
        ) : null}
        {!initialListLoading && displayOptions.map((option, idx) => {
          const text = resolveDropdownOptionText(option)
          const titleText = text.trim()
          const selected = isOptionSelected(option)

          return (
            <li
              key={option.id != null ? String(option.id) : `${text}-${idx}`}
              role="option"
              aria-selected={selected}
              title={titleText || undefined}
              className={`searchable-dropdown-option${option.subLabel ? " searchable-dropdown-option--stacked" : ""}${idx === activeIndex ? " is-active" : ""}${selected ? " is-selected" : ""}`}
              onMouseDown={(event) => selectOption(event, option)}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              {option.subLabel ? (
                <span className="searchable-dropdown-option-stack">
                  <span className="searchable-dropdown-option-label">{text}</span>
                  <span className="searchable-dropdown-option-sub">{option.subLabel}</span>
                </span>
              ) : (
                <span>{text}</span>
              )}

              {/* {text === value && (
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M7.5 10l2 2 3-4"
                    stroke="#10b981"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )} */}
            </li>
          )
        })}
        {!listLoading && displayOptions.length === 0 && query && !showCustomOption && emptyResultsMessage ? (
          <li className="searchable-dropdown-empty" role="presentation">
            {emptyResultsMessage}
          </li>
        ) : null}
        {!listLoading && displayOptions.length === 0 && query && showCustomOption && (
          <li
            role="option"
            className={`searchable-dropdown-option${
              activeIndex === displayOptions.length ? " is-active" : ""
            }`}
            onMouseDown={(event) =>
              selectOption(event, { label: query.trim(), value: query.trim() })
            }
            onMouseEnter={() => setActiveIndex(displayOptions.length)}
          >
            <span>Use &quot;{query.trim()}&quot;</span>
          </li>
        )}
        {hasMore && displayOptions.length > 0 && !initialListLoading && !isSearching ? (
          <li
            ref={loadMoreSentinelRef}
            className="searchable-dropdown-load-sentinel"
            aria-hidden
          />
        ) : null}
        {loadingMore && (
          <li className="searchable-dropdown-load-more" aria-live="polite">
            Loading more...
          </li>
        )}
      </ul>
    </div>
  )

  return (
    <div
      className={`searchable-dropdown-wrapper${wrapperClassName ? ` ${wrapperClassName}` : ""}${valueSubLabel ? " searchable-dropdown-wrapper--stacked-value" : ""}`}
      ref={wrapperRef}
    >
      {!hideLabel && label ? (
        <label htmlFor={id} className="searchable-dropdown-label">
          <span className="searchable-dropdown-label-text">
            {label} {required && <span className="required-indicator">*</span>}
          </span>
          {tooltip ? (
            <HoverTooltip
              content={tooltip}
              placement="above"
              strategy="portal"
              align="start"
              maxWidth={360}
              wrapClassName="searchable-dropdown-label-tooltip"
              ariaLabel="Field help"
            >
              <span className="searchable-dropdown-tooltip-trigger" tabIndex={0} role="button">
                <FieldLabelInfoIcon />
              </span>
            </HoverTooltip>
          ) : null}
        </label>
      ) : null}
      <div className="searchable-dropdown-container" ref={containerRef}>
        <button
          type="button"
          id={id}
          className={`searchable-dropdown-trigger ${disabled ? "disabled" : ""} ${error ? "input-error" : ""}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-label={ariaFieldLabel}
          aria-required={required}
          onMouseDown={(event) => {
            if (open) event.preventDefault()
          }}
          onClick={handleDropdownClick}
          onKeyDown={hideSearch && open ? onKeyDown : undefined}
          disabled={disabled}
        >
          {valueSubLabel ? (
            <span className="searchable-dropdown-selected-stack">
              <span className={`searchable-dropdown-selected ${!displayValue ? "placeholder" : ""}`} title={displayValue || undefined}>
                {displayValue || placeholder}
              </span>
              {displayValue ? (
                <span className="searchable-dropdown-selected-sub">{valueSubLabel}</span>
              ) : null}
            </span>
          ) : (
            <span className={`searchable-dropdown-selected ${!displayValue ? "placeholder" : ""}`} title={displayValue || undefined}>
              {displayValue || placeholder}
            </span>
          )}
          <svg
            aria-hidden="true"
            width="16"
            height="16"
            viewBox="0 0 20 20"
            fill="none"
            className="searchable-dropdown-caret"
          >
            <path d="M5 7l5 5 5-5" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {open &&
          (menuPortal && typeof document !== "undefined"
            ? createPortal(popoverNode, document.body)
            : popoverNode)}
      </div>
      {error && <p className="error-text">{error}</p>}
    </div>
  )
}

export default SearchableDropdown
